// Phase 23.E — Advanced Conditions section inside the Rule Builder.
//
// Renders the new `match.contexts[]` block on each rule with:
//   • a collapsible section (collapsed by default — most rules need no contexts)
//   • dropdowns narrowed by the rule's selected categories via /api/contexts/relevant
//   • a per-condition row: context type / operator / values (chips) / required
//   • a curated path (2 visible named types: Portal/Channel + Subcategory) and a
//     "Add Custom Context" escape hatch for *brand-new dimensions* only
//   • inline "+ Add value" inside the curated picker so admins can promote a
//     missing value to the curated registry without leaving the Rule Builder
//     (POST /api/admin/context-definitions, auto-selects on success)
//   • a live specificity badge (Low / Medium / High)
//   • a human-readable "Rule matches when…" preview
//   • an in-builder match simulator showing 3 ✓ + 3 ✗ synthetic examples
//   • a rename/delete overflow menu on admin-created values to prevent
//     registry pollution from accidental typos
//
// Engine semantics mirrored verbatim from services/context_matcher.py:
//   AND across conditions • OR within values • required flag • in/not_in operators.
// If this mirror ever drifts, the live preview will lie — keep them in sync.
//
// Three curated types (transaction_type, spend_nature, ecosystem) are
// intentionally HIDDEN from this UI for now (locked product decision —
// they overlap with existing Category/Merchant/International fields). The
// backend schema / matcher / resolver still understand them; we just don't
// surface them in the Add-Condition dropdown.
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Settings2, Plus, Trash2, ChevronDown, ChevronRight, Sparkles, CheckCircle2,
  XCircle, Gauge, Info, FlaskConical, MoreVertical, Pencil, Loader2,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { AdminAPI } from '@/lib/admin-api';
import { toast } from 'sonner';

// ──────────────────────────────────────────────────────────────────────
// Curated type list. Locked product decision (May 2026): only Portal/Channel
// and Subcategory are surfaced in the Rule Builder. transaction_type,
// spend_nature and ecosystem remain seeded server-side for the matcher
// but stay hidden from this UI until a real card-program use case appears.
// ──────────────────────────────────────────────────────────────────────
const CURATED_TYPES = [
  { value: 'portal_channel', label: 'Portal / Channel', hint: 'Where the transaction is initiated (SmartBuy, OTA, wallet, airline-direct, etc.)' },
  { value: 'subcategory',    label: 'Subcategory', hint: 'Sub-segment within the selected portal/channel (flight, hotel, rail, cab…)' },
];

// All five backend-known curated types — kept here so the Custom Context
// input can refuse to shadow any of them, even the three we currently hide.
// `travel_type` is intentionally kept in the reserved set as a deprecated
// alias so admins can't accidentally create `custom:travel_type` during the
// rename transition. Drop it once we're sure no client still sends it.
const RESERVED_CURATED_KEYS = new Set([
  'portal_channel', 'subcategory', 'transaction_type', 'spend_nature', 'ecosystem',
  'travel_type',  // legacy — pre-May-2026 name for subcategory
]);

const OPERATORS = [
  { value: 'in',     label: 'IS one of'   },
  { value: 'not_in', label: 'IS NOT one of' },
];

// Snake_case → Title Case label. Preserves a small set of well-known
// acronyms (SmartBuy, OTA, BBPS, UPI, POS, ATM, DCC, EMI) so the
// auto-generated label reads naturally instead of "Smartbuy".
const ACRONYMS = {
  smartbuy: 'SmartBuy', ota: 'OTA', bbps: 'BBPS', upi: 'UPI',
  pos: 'POS', atm: 'ATM', dcc: 'DCC', emi: 'EMI', neufuel: 'NeuFuel',
  payzapp: 'PayZapp', gpay: 'GPay', mmt: 'MMT', goibibo: 'Goibibo',
  ixigo: 'Ixigo', edge: 'EDGE', amex: 'Amex', hdfc: 'HDFC', icici: 'ICICI',
  sbi: 'SBI', rbl: 'RBL', idfc: 'IDFC',
};
function labelFromSnake(value) {
  return String(value || '')
    .split('_')
    .filter(Boolean)
    .map((w) => ACRONYMS[w.toLowerCase()] || (w[0].toUpperCase() + w.slice(1).toLowerCase()))
    .join(' ');
}

// True for values whose `id` matches the deterministic seed pattern
// `ctxd-{context_type}-{value}` — these are SEED data; the Rule Builder
// must not let admins delete them (they ship with the platform). Values
// created via POST /api/admin/context-definitions get UUID-suffixed ids
// (e.g. `ctxd-9f3a2b4e1c`) and ARE eligible for rename/delete.
function isSeeded(def) {
  if (!def?.id || !def?.context_type || !def?.value) return false;
  return def.id === `ctxd-${def.context_type}-${def.value}`;
}


// ──────────────────────────────────────────────────────────────────────
// Pure mirror of /services/context_matcher.py — keep in lockstep with backend.
// Returns { matched: bool, specificity: number, failureReason?: string }.
// ──────────────────────────────────────────────────────────────────────
function evaluateContexts(conditions, runtimeContexts) {
  if (!conditions || conditions.length === 0) return { matched: true, specificity: 0 };
  let spec = 0;
  for (let i = 0; i < conditions.length; i++) {
    const cond = conditions[i];
    const key = cond.context_type === 'custom' ? `custom:${cond.custom_key || ''}` : cond.context_type;
    const runtimeValues = (runtimeContexts[key] || []).map((v) => String(v).toLowerCase());
    const condValues = (cond.values || []).map((v) => String(v).toLowerCase());
    const required = cond.required !== false;
    const operator = cond.operator || 'in';
    if (runtimeValues.length === 0) {
      if (required) return { matched: false, failureReason: `Missing context ${key}` };
      continue;
    }
    if (operator === 'in') {
      if (condValues.length > 0 && !runtimeValues.some((rv) => condValues.includes(rv))) {
        return { matched: false, failureReason: `${key} not in {${cond.values.join(', ')}}` };
      }
    } else if (operator === 'not_in') {
      if (condValues.length > 0 && runtimeValues.some((rv) => condValues.includes(rv))) {
        return { matched: false, failureReason: `${key} hit forbidden set` };
      }
    }
    if (required) spec += 0.10;
  }
  return { matched: true, specificity: spec };
}


// ──────────────────────────────────────────────────────────────────────
// Friendly label lookup. Falls back to value when the registry doesn't
// have the value (e.g. soft-deleted custom value the rule still references).
// ──────────────────────────────────────────────────────────────────────
function valueLabel(registry, ctype, value) {
  const defs = (registry?.[ctype] || []);
  const hit = defs.find((d) => d.value === value);
  return hit?.label || labelFromSnake(value);
}
function typeLabel(ctype) {
  const named = CURATED_TYPES.find((t) => t.value === ctype);
  if (named) return named.label;
  return ctype.startsWith('custom:') ? `Custom · ${ctype.slice('custom:'.length)}` : ctype;
}


// ──────────────────────────────────────────────────────────────────────
// Specificity badge — Low / Medium / High based on the locked engine
// scoring (legacy weights + Phase-23 context weight).
// ──────────────────────────────────────────────────────────────────────
function specificityBadge(conditions, legacyMatch) {
  const requiredContextCount = (conditions || []).filter((c) => c.required !== false).length;
  const mccCount       = (legacyMatch?.mcc       || []).length;
  const merchantsCount = (legacyMatch?.merchants || []).length;
  const categoryCount  = (legacyMatch?.categories|| []).length;
  // Mirror backend specificity weighting (rules_engine._confidence + _rule_specificity)
  const score = (
    mccCount * 0.05
    + merchantsCount * 0.07
    + categoryCount * 0.03
    + requiredContextCount * 0.10
  );
  if (score < 0.10) return { label: 'Low',    tone: 'amber',   score };
  if (score < 0.30) return { label: 'Medium', tone: 'teal',    score };
  return                    { label: 'High',   tone: 'emerald', score };
}


// ──────────────────────────────────────────────────────────────────────
// Match-Simulator preview — synthesize a handful of representative
// runtime contexts and run them through the matcher mirror. Bounded
// to 3 ✓ + 3 ✗ for visual clarity.
// ──────────────────────────────────────────────────────────────────────
function buildSimulatorExamples(conditions, registry) {
  if (!conditions || conditions.length === 0) return { matches: [], misses: [] };
  const baseValues = {};
  for (const cond of conditions) {
    const key = cond.context_type === 'custom' ? `custom:${cond.custom_key || ''}` : cond.context_type;
    if (cond.operator === 'not_in') {
      const reg = registry?.[cond.context_type] || [];
      const safe = reg.find((d) => !cond.values.includes(d.value));
      baseValues[key] = safe ? [safe.value] : ['__unmatched__'];
    } else {
      baseValues[key] = (cond.values || []).length ? [cond.values[0]] : [];
    }
  }
  const exampleLabel = (runtime) => {
    const parts = Object.entries(runtime).map(([key, vals]) => {
      const ctype = key.startsWith('custom:') ? 'custom' : key;
      const val = vals[0];
      const label = ctype === 'custom' ? val : valueLabel(registry, ctype, val);
      return label;
    });
    return parts.join(' + ');
  };
  const matches = [];
  const baseRuntime = { ...baseValues };
  matches.push({ key: 'base', label: exampleLabel(baseRuntime), runtime: baseRuntime });
  for (const cond of conditions) {
    if (cond.operator === 'in' && (cond.values || []).length > 1 && matches.length < 3) {
      const key = cond.context_type === 'custom' ? `custom:${cond.custom_key || ''}` : cond.context_type;
      const variant = { ...baseRuntime, [key]: [cond.values[1]] };
      matches.push({ key: `variant-${matches.length}`, label: exampleLabel(variant), runtime: variant });
    }
  }
  const misses = [];
  for (const cond of conditions) {
    if (misses.length >= 3) break;
    const key = cond.context_type === 'custom' ? `custom:${cond.custom_key || ''}` : cond.context_type;
    const reg = registry?.[cond.context_type] || [];
    let violatingValue;
    if (cond.operator === 'in') {
      const out = reg.find((d) => !cond.values.includes(d.value));
      violatingValue = out?.value;
    } else {
      violatingValue = cond.values[0];
    }
    if (!violatingValue) continue;
    const missRuntime = { ...baseRuntime, [key]: [violatingValue] };
    misses.push({
      key: `miss-${cond.context_type}-${violatingValue}`,
      label: exampleLabel(missRuntime),
      runtime: missRuntime,
      violated: cond.context_type,
    });
  }
  return { matches, misses };
}


// ──────────────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────────────
export const AdvancedConditions = ({
  ruleId,
  conditions,           // rule.match.contexts
  legacyMatch,          // { categories, mcc, merchants, mode, international }
  onChange,             // (next list)
  locked,
}) => {
  const conditionsCount = (conditions || []).length;
  const [open, setOpen] = useState(conditionsCount > 0);
  const [registry, setRegistry] = useState(null);
  const [registryErr, setRegistryErr] = useState(null);
  const [showCustomPrompt, setShowCustomPrompt] = useState(false);
  // Bump this to force the registry to refetch after admins create/edit
  // values inline. We can't just call load() because the effect's deps
  // array would need to include it — clearer to maintain a counter.
  const [registryNonce, setRegistryNonce] = useState(0);
  const refetchRegistry = () => setRegistryNonce((n) => n + 1);

  // Fetch the curated taxonomy. When the rule has a single category set,
  // narrow the dropdowns to that category's relevant values. Multi-category
  // rules get the union of values. When no category is set we fall back to
  // the full registry so admins building a generic rule still see everything.
  useEffect(() => {
    let cancelled = false;
    const cats = legacyMatch?.categories || [];
    async function load() {
      try {
        if (cats.length === 0) {
          const r = await AdminAPI.contexts.registry();
          if (!cancelled) setRegistry(r.definitions || {});
          return;
        }
        const all = await Promise.all(cats.map((c) => AdminAPI.contexts.relevant(c)));
        if (cancelled) return;
        const merged = {};
        const dedupe = new Set();
        for (const r of all) {
          for (const [ctype, defs] of Object.entries(r.definitions || {})) {
            if (!merged[ctype]) merged[ctype] = [];
            for (const d of defs) {
              const k = `${ctype}::${d.value}`;
              if (dedupe.has(k)) continue;
              dedupe.add(k);
              merged[ctype].push(d);
            }
          }
        }
        for (const ctype of Object.keys(merged)) {
          merged[ctype].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        }
        setRegistry(merged);
      } catch (e) {
        if (!cancelled) setRegistryErr(String(e?.message || e));
      }
    }
    load();
    return () => { cancelled = true; };
  }, [JSON.stringify(legacyMatch?.categories || []), registryNonce]);

  const list = conditions || [];

  const setList = (next) => onChange(next);
  const updateCondition = (idx, patch) => {
    const next = list.map((c, i) => (i === idx ? { ...c, ...patch } : c));
    setList(next);
  };
  const removeCondition = (idx) => setList(list.filter((_, i) => i !== idx));

  const addCondition = (context_type, custom_key) => {
    const next = [
      ...list,
      {
        context_type,
        custom_key: context_type === 'custom' ? (custom_key || '') : null,
        operator: 'in',
        values: [],
        required: true,
      },
    ];
    setList(next);
    if (!open) setOpen(true);
  };

  const badge = useMemo(
    () => specificityBadge(list, legacyMatch),
    [JSON.stringify(list), JSON.stringify(legacyMatch || {})],
  );

  const { matches, misses } = useMemo(
    () => buildSimulatorExamples(list, registry),
    [JSON.stringify(list), JSON.stringify(registry || {})],
  );

  const summaryLines = useMemo(() => {
    const lines = [];
    const cats = legacyMatch?.categories || [];
    if (cats.length) lines.push({ key: 'cat', text: <>Category <b>=</b> {cats.join(' | ')}</> });
    const mode = legacyMatch?.mode;
    if (mode && mode !== 'any') lines.push({ key: 'mode', text: <>Mode <b>=</b> {mode}</> });
    if (legacyMatch?.international === true) lines.push({ key: 'intl', text: <>International only</> });
    if (legacyMatch?.international === false) lines.push({ key: 'intl', text: <>Domestic only</> });
    for (const cond of list) {
      const ctype = cond.context_type;
      const labelType = ctype === 'custom' ? `${cond.custom_key || 'custom'}` : typeLabel(ctype);
      const op = cond.operator === 'not_in' ? '∉' : '∈';
      const vlabels = (cond.values || []).map((v) => (ctype === 'custom' ? v : valueLabel(registry, ctype, v))).join(', ');
      const reqMark = cond.required === false ? ' (optional)' : '';
      lines.push({
        key: `c-${ctype}-${cond.custom_key || ''}`,
        text: <>{labelType} <b>{op}</b> {`{${vlabels || 'no values yet'}}`}{reqMark}</>,
      });
    }
    return lines;
  }, [JSON.stringify(list), JSON.stringify(legacyMatch || {}), JSON.stringify(registry || {})]);

  return (
    <details
      className={cn(
        'rounded-md border border-dashed border-border bg-[hsl(var(--surface-2))]/40 p-2.5',
        list.length > 0 && 'border-solid border-primary/30 bg-primary/5',
      )}
      open={open}
      onToggle={(e) => setOpen(e.target.open)}
      data-testid={`admin-rule-builder-advanced-${ruleId}`}
    >
      <summary className="flex items-center justify-between cursor-pointer list-none gap-2">
        <div className="flex items-center gap-1.5 text-[11px] font-medium">
          {open ? <ChevronDown className="h-3 w-3 text-accent" /> : <ChevronRight className="h-3 w-3 text-accent" />}
          <Settings2 className="h-3 w-3 text-accent" />
          Transaction Context
          {list.length > 0 && (
            <Badge variant="outline" className="text-[10px] gap-1 ml-1 px-1.5" data-testid={`adv-count-${ruleId}`}>
              {list.length} condition{list.length === 1 ? '' : 's'}
            </Badge>
          )}
          {list.length === 0 && (
            <span className="text-[10px] text-muted-foreground italic font-normal">
              Optional — narrow this rule beyond category & merchant.
            </span>
          )}
        </div>
        <SpecificityBadge meta={badge} />
      </summary>

      <div className="mt-3 space-y-3">
        {registryErr && (
          <div className="text-[11px] text-rose-500" data-testid={`adv-registry-error-${ruleId}`}>
            Failed to load context registry: {registryErr}
          </div>
        )}

        {/* Live preview */}
        <div className="rounded-md border border-border bg-background/60 px-3 py-2" data-testid={`adv-preview-${ruleId}`}>
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1.5">
            <Sparkles className="h-3 w-3" /> Rule matches when
          </div>
          {summaryLines.length === 0 ? (
            <div className="text-[11px] text-muted-foreground italic">
              No criteria yet — rule will not fire until at least one condition or category is set.
            </div>
          ) : (
            <ol className="text-[12px] leading-relaxed">
              {summaryLines.map((l, i) => (
                <li key={l.key} className="flex gap-1.5 items-baseline">
                  <span className="text-muted-foreground font-mono-num shrink-0 text-[10px]">
                    {i === 0 ? '•' : 'AND'}
                  </span>
                  <span className="min-w-0">{l.text}</span>
                </li>
              ))}
            </ol>
          )}
        </div>

        {/* Conditions list */}
        {list.length === 0 ? (
          <div className="rounded-md border border-dashed border-border bg-background/40 px-3 py-4 text-center" data-testid={`adv-empty-${ruleId}`}>
            <div className="text-[11px] text-muted-foreground">
              Add a context condition to narrow this rule beyond category alone.
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {list.map((cond, idx) => (
              <ConditionRow
                key={`${cond.context_type}-${cond.custom_key || ''}-${idx}`}
                ruleId={ruleId}
                idx={idx}
                cond={cond}
                registry={registry}
                locked={locked}
                onChange={(patch) => updateCondition(idx, patch)}
                onRemove={() => removeCondition(idx)}
                onRegistryChanged={refetchRegistry}
              />
            ))}
          </div>
        )}

        {/* Add condition / Add custom */}
        <div className="flex items-center gap-2 flex-wrap">
          <AddConditionDropdown
            ruleId={ruleId}
            disabled={locked}
            existing={list}
            onAdd={(ctype) => addCondition(ctype, null)}
          />
          {!showCustomPrompt ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={locked}
              onClick={() => setShowCustomPrompt(true)}
              className="gap-1 text-[11px] text-muted-foreground"
              data-testid={`adv-show-custom-${ruleId}`}
              title="Use Custom Context only for a brand-new dimension that doesn't fit Portal/Channel or Subcategory."
            >
              <Plus className="h-3 w-3" /> Add Custom Context
            </Button>
          ) : (
            <CustomContextRow
              ruleId={ruleId}
              disabled={locked}
              existing={list}
              onCancel={() => setShowCustomPrompt(false)}
              onConfirm={(customKey) => {
                addCondition('custom', customKey);
                setShowCustomPrompt(false);
              }}
            />
          )}
        </div>

        {/* In-builder match simulator */}
        {list.length > 0 && (matches.length > 0 || misses.length > 0) && (
          <Card className="p-3 border-dashed bg-background/40" data-testid={`adv-simulator-${ruleId}`}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-2">
              <FlaskConical className="h-3 w-3 text-accent" /> Match preview
              <span className="ml-auto inline-flex items-center gap-1 normal-case tracking-normal text-[10px] text-muted-foreground">
                <Info className="h-3 w-3" /> Synthetic — uses your current conditions
              </span>
            </div>
            {matches.length > 0 && (
              <div className="space-y-1">
                {matches.map((ex) => {
                  const res = evaluateContexts(list, ex.runtime);
                  return (
                    <div key={ex.key} className="flex items-start gap-2 text-[12px]">
                      <CheckCircle2 className={cn('h-3.5 w-3.5 shrink-0 mt-0.5', res.matched ? 'text-emerald-500' : 'text-rose-500')} />
                      <span className="text-foreground">This rule would {res.matched ? '' : 'NOT '}match: <span className="font-medium">{ex.label}</span></span>
                    </div>
                  );
                })}
              </div>
            )}
            {misses.length > 0 && (
              <div className="space-y-1 mt-2">
                {misses.map((ex) => {
                  const res = evaluateContexts(list, ex.runtime);
                  return (
                    <div key={ex.key} className="flex items-start gap-2 text-[12px]">
                      <XCircle className={cn('h-3.5 w-3.5 shrink-0 mt-0.5', !res.matched ? 'text-rose-500' : 'text-emerald-500')} />
                      <span className="text-muted-foreground">This rule would {res.matched ? '' : 'NOT '}match: <span className="font-medium">{ex.label}</span></span>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        )}
      </div>
    </details>
  );
};


// ──────────────────────────────────────────────────────────────────────
// Specificity badge sub-component (visual only)
// ──────────────────────────────────────────────────────────────────────
const TONE = {
  emerald: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
  teal:    'bg-primary/10 text-primary border-primary/30',
  amber:   'bg-amber-500/10 text-amber-600 border-amber-500/30',
};
function SpecificityBadge({ meta }) {
  return (
    <Badge
      variant="outline"
      className={cn('text-[10px] gap-1 uppercase tracking-wide px-1.5', TONE[meta.tone])}
      data-testid={`adv-specificity-${meta.label.toLowerCase()}`}
      title={`Specificity score: ${meta.score.toFixed(2)} — higher specificity wins tie-breaks at the same priority`}
    >
      <Gauge className="h-3 w-3" /> {meta.label} specificity
    </Badge>
  );
}


// ──────────────────────────────────────────────────────────────────────
// One condition row.
// ──────────────────────────────────────────────────────────────────────
const ConditionRow = ({ ruleId, idx, cond, registry, locked, onChange, onRemove, onRegistryChanged }) => {
  const ctype = cond.context_type;
  const isCustom = ctype === 'custom';
  const registryEntries = isCustom ? [] : (registry?.[ctype] || []);
  const selected = new Set((cond.values || []).map((v) => String(v).toLowerCase()));

  const toggleValue = (v) => {
    const k = String(v).toLowerCase();
    if (selected.has(k)) {
      onChange({ values: (cond.values || []).filter((x) => String(x).toLowerCase() !== k) });
    } else {
      onChange({ values: [...(cond.values || []), v] });
    }
  };
  const addCustomValue = (raw) => {
    const value = (raw || '').trim();
    if (!value) return;
    if ((cond.values || []).some((x) => String(x).toLowerCase() === value.toLowerCase())) return;
    onChange({ values: [...(cond.values || []), value] });
  };

  return (
    <div className="rounded-md border border-border bg-background/70 p-2.5 space-y-2" data-testid={`adv-condition-${ruleId}-${idx}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant="outline" className="text-[10px] px-1.5">
          {isCustom ? `Custom · ${cond.custom_key || ''}` : typeLabel(ctype)}
        </Badge>
        <Select value={cond.operator || 'in'} onValueChange={(v) => onChange({ operator: v })} disabled={locked}>
          <SelectTrigger className="h-7 w-[140px] text-xs" data-testid={`adv-operator-${ruleId}-${idx}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {OPERATORS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-1.5 rounded border border-border bg-[hsl(var(--surface-2))] px-2 py-0.5">
          <Label className="text-[10px] uppercase tracking-wider">Required</Label>
          <Switch
            checked={cond.required !== false}
            disabled={locked}
            onCheckedChange={(v) => onChange({ required: v })}
            data-testid={`adv-required-${ruleId}-${idx}`}
          />
        </div>
        <Button
          variant="ghost"
          size="icon"
          disabled={locked}
          onClick={onRemove}
          className="h-7 w-7 text-muted-foreground hover:text-rose-500"
          data-testid={`adv-remove-${ruleId}-${idx}`}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>

      {/* Value chips — curated picker for named types, free-text for custom */}
      {isCustom ? (
        <CustomValuesPicker
          values={cond.values || []}
          locked={locked}
          ruleId={ruleId}
          idx={idx}
          onAdd={addCustomValue}
          onRemove={(v) => toggleValue(v)}
        />
      ) : (
        <CuratedValuesPicker
          ruleId={ruleId}
          idx={idx}
          ctype={ctype}
          registryEntries={registryEntries}
          selected={selected}
          onToggle={toggleValue}
          locked={locked}
          onRegistryChanged={onRegistryChanged}
        />
      )}
      {(cond.values || []).length === 0 && (
        <div className="text-[10px] text-amber-600 dark:text-amber-400" data-testid={`adv-values-warning-${ruleId}-${idx}`}>
          Pick at least one value, otherwise this condition matches anything.
        </div>
      )}
    </div>
  );
};


// ──────────────────────────────────────────────────────────────────────
// Curated value picker — checkbox-style chips from the registry +
// inline "+ Add value" affordance + rename/delete overflow on admin-
// created values to prevent typo pollution.
// ──────────────────────────────────────────────────────────────────────
const CuratedValuesPicker = ({ ruleId, idx, ctype, registryEntries, selected, onToggle, locked, onRegistryChanged }) => {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [creating, setCreating] = useState(false);
  const inputRef = useRef(null);

  const normalizeValue = (raw) => String(raw || '').toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');

  const handleCreate = async () => {
    const value = normalizeValue(draft);
    if (!value) {
      toast.error('Value must contain letters or numbers');
      return;
    }
    // Soft duplicate check — backend has a hard unique index too, but
    // catching it here is faster + lets us auto-select the existing chip
    // instead of showing a confusing 409.
    const existing = registryEntries.find((d) => d.value === value);
    if (existing) {
      toast.info(`'${existing.label}' already exists — selecting it.`);
      onToggle(existing.value);
      setAdding(false);
      setDraft('');
      return;
    }
    setCreating(true);
    try {
      const body = {
        context_type: ctype,
        value,
        label: labelFromSnake(value),
        scope: ['transaction'],
        is_active: true,
      };
      await AdminAPI.contexts.create(body);
      toast.success(`Added '${body.label}' to ${typeLabel(ctype)} registry`);
      // Refresh the registry; auto-select once it reappears.
      await onRegistryChanged?.();
      // The new chip will appear after registry refetch; auto-select via the
      // raw value (the registry update is async, but onToggle works against
      // the value string regardless of whether the chip is rendered yet).
      onToggle(value);
      setAdding(false);
      setDraft('');
    } catch (e) {
      const msg = String(e?.message || e || '');
      if (msg.includes('409') || msg.toLowerCase().includes('already exists')) {
        toast.info('Value already exists — selecting it.');
        onToggle(value);
        setAdding(false);
        setDraft('');
        await onRegistryChanged?.();
      } else if (msg.includes('403')) {
        toast.error(`You don't have permission to add values in this workspace.`);
      } else {
        toast.error(`Couldn't create value: ${msg}`);
      }
    } finally {
      setCreating(false);
    }
  };

  if ((!registryEntries || registryEntries.length === 0) && !adding) {
    return (
      <div className="space-y-1.5">
        <div className="text-[11px] text-muted-foreground italic">
          No curated values for this category narrowing.
        </div>
        {!locked && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => { setAdding(true); setTimeout(() => inputRef.current?.focus(), 0); }}
            className="gap-1 text-[11px] h-7"
            data-testid={`adv-add-value-empty-${ruleId}-${idx}`}
          >
            <Plus className="h-3 w-3" /> Add the first value
          </Button>
        )}
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`adv-values-picker-${ruleId}-${idx}`}>
      {registryEntries.map((d) => (
        <ValueChip
          key={d.value}
          def={d}
          isOn={selected.has(d.value.toLowerCase())}
          locked={locked}
          ruleId={ruleId}
          idx={idx}
          onToggle={() => onToggle(d.value)}
          onRegistryChanged={onRegistryChanged}
        />
      ))}
      {!locked && !adding && (
        <button
          type="button"
          onClick={() => { setAdding(true); setTimeout(() => inputRef.current?.focus(), 0); }}
          className="inline-flex items-center gap-1 rounded-md border border-dashed border-input bg-background px-2 py-1 text-[11px] text-muted-foreground hover:bg-muted/40 hover:text-foreground transition-colors"
          data-testid={`adv-add-value-${ruleId}-${idx}`}
          title={`Add a new value to ${typeLabel(ctype)} registry`}
        >
          <Plus className="h-3 w-3" /> Add value
        </button>
      )}
      {adding && (
        <div className="inline-flex items-center gap-1 rounded-md border border-primary/40 bg-primary/5 px-2 py-0.5">
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(normalizeValue(e.target.value))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); handleCreate(); }
              if (e.key === 'Escape') { e.preventDefault(); setAdding(false); setDraft(''); }
            }}
            placeholder="axis_travel_edge"
            disabled={creating}
            className="w-[160px] bg-transparent text-[11px] font-mono-num outline-none placeholder:text-muted-foreground/70"
            data-testid={`adv-add-value-input-${ruleId}-${idx}`}
          />
          {draft && (
            <span className="text-[10px] text-muted-foreground border-l border-border pl-1.5 ml-0.5">
              → {labelFromSnake(draft)}
            </span>
          )}
          <button
            type="button"
            onClick={handleCreate}
            disabled={creating || !draft}
            className="text-primary hover:text-primary/80 disabled:opacity-40 px-1 text-[14px] leading-none"
            data-testid={`adv-add-value-confirm-${ruleId}-${idx}`}
            title="Create and select"
          >
            {creating ? <Loader2 className="h-3 w-3 animate-spin" /> : '✓'}
          </button>
          <button
            type="button"
            onClick={() => { setAdding(false); setDraft(''); }}
            disabled={creating}
            className="text-muted-foreground hover:text-rose-500 disabled:opacity-40 px-1 text-[14px] leading-none"
            data-testid={`adv-add-value-cancel-${ruleId}-${idx}`}
            title="Cancel"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};


// ──────────────────────────────────────────────────────────────────────
// One value chip — seeded values are read-only; admin-created values
// get a rename/delete overflow menu so typos are correctable on the spot.
// ──────────────────────────────────────────────────────────────────────
const ValueChip = ({ def, isOn, locked, ruleId, idx, onToggle, onRegistryChanged }) => {
  const seeded = isSeeded(def);
  const canEdit = !seeded && !locked;
  return (
    <div className="inline-flex items-stretch">
      <button
        type="button"
        disabled={locked}
        onClick={onToggle}
        title={def.description || def.label}
        data-testid={`adv-value-${ruleId}-${idx}-${def.value}`}
        className={cn(
          'inline-flex items-center gap-1 border px-2 py-1 text-[11px] transition-colors',
          canEdit ? 'rounded-l-md border-r-0' : 'rounded-md',
          isOn
            ? 'bg-primary/15 border-primary/40 text-primary font-medium'
            : 'bg-background border-input text-muted-foreground hover:bg-muted/40',
          locked && 'opacity-60 cursor-not-allowed',
        )}
      >
        {def.label}
      </button>
      {canEdit && (
        <ValueChipMenu
          def={def}
          isOn={isOn}
          onRegistryChanged={onRegistryChanged}
          testId={`adv-value-menu-${ruleId}-${idx}-${def.value}`}
        />
      )}
    </div>
  );
};


// ──────────────────────────────────────────────────────────────────────
// Rename / Delete overflow for admin-created values.
// ──────────────────────────────────────────────────────────────────────
const ValueChipMenu = ({ def, isOn, onRegistryChanged, testId }) => {
  const [open, setOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [draftLabel, setDraftLabel] = useState(def.label || '');
  const [busy, setBusy] = useState(false);

  const handleRename = async () => {
    const newLabel = (draftLabel || '').trim();
    if (!newLabel || newLabel === def.label) {
      setRenaming(false);
      return;
    }
    setBusy(true);
    try {
      await AdminAPI.contexts.update(def.id, { label: newLabel });
      toast.success(`Renamed to '${newLabel}'`);
      setRenaming(false);
      setOpen(false);
      await onRegistryChanged?.();
    } catch (e) {
      toast.error(`Couldn't rename: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete '${def.label}' from registry? Rules already using it will keep working but the value will hide from new selections.`)) {
      return;
    }
    setBusy(true);
    try {
      await AdminAPI.contexts.remove(def.id);
      toast.success(`Deleted '${def.label}'`);
      setOpen(false);
      await onRegistryChanged?.();
    } catch (e) {
      toast.error(`Couldn't delete: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setRenaming(false); setDraftLabel(def.label || ''); } }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center justify-center border border-l-0 rounded-r-md px-1.5 text-[11px] transition-colors',
            isOn
              ? 'bg-primary/15 border-primary/40 text-primary'
              : 'bg-background border-input text-muted-foreground hover:bg-muted/40',
          )}
          title="Rename or delete this value"
          data-testid={testId}
        >
          <MoreVertical className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[260px] p-2" data-testid={`${testId}-content`}>
        <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground px-1 pb-1.5 border-b border-border">
          {def.value}
        </div>
        {!renaming ? (
          <div className="mt-1.5 space-y-0.5">
            <button
              type="button"
              onClick={() => { setRenaming(true); setDraftLabel(def.label || ''); }}
              className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded text-[12px] hover:bg-muted/50"
              data-testid={`${testId}-rename`}
            >
              <Pencil className="h-3 w-3" /> Rename label
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={busy}
              className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded text-[12px] text-rose-500 hover:bg-rose-500/10 disabled:opacity-40"
              data-testid={`${testId}-delete`}
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />} Delete from registry
            </button>
            <div className="text-[10px] text-muted-foreground px-2 pt-1.5 leading-snug border-t border-border mt-1">
              Existing rules keep working — deleting only hides this value from new selections.
            </div>
          </div>
        ) : (
          <div className="mt-1.5 space-y-1.5">
            <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">New label</Label>
            <Input
              autoFocus
              value={draftLabel}
              onChange={(e) => setDraftLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setRenaming(false); }}
              disabled={busy}
              className="h-7 text-[11px]"
              data-testid={`${testId}-rename-input`}
            />
            <div className="flex items-center justify-end gap-1.5">
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => setRenaming(false)} className="h-7 text-[11px]">Cancel</Button>
              <Button size="sm" disabled={busy || !draftLabel.trim()} onClick={handleRename} className="h-7 text-[11px] gap-1" data-testid={`${testId}-rename-confirm`}>
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : null} Save
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};


// ──────────────────────────────────────────────────────────────────────
// Free-text values for custom contexts (no curation).
// ──────────────────────────────────────────────────────────────────────
const CustomValuesPicker = ({ values, locked, ruleId, idx, onAdd, onRemove }) => {
  const [draft, setDraft] = useState('');
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-1.5">
        {values.map((v) => (
          <Badge
            key={v}
            variant="outline"
            className="text-[10px] font-mono-num h-6 gap-1 pr-1 inline-flex items-center"
            data-testid={`adv-custom-value-${ruleId}-${idx}-${v}`}
          >
            <span className="truncate max-w-[160px]">{v}</span>
            {!locked && (
              <button onClick={() => onRemove(v)} className="shrink-0 hover:text-rose-500" aria-label={`Remove ${v}`}>×</button>
            )}
          </Badge>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ',') && draft.trim()) {
              e.preventDefault();
              onAdd(draft);
              setDraft('');
            }
          }}
          onBlur={() => { if (draft.trim()) { onAdd(draft); setDraft(''); } }}
          placeholder="Type a custom value and press Enter"
          disabled={locked}
          className="flex-1 min-w-[160px] bg-transparent text-xs outline-none placeholder:text-muted-foreground border border-input rounded-md px-2 py-1"
          data-testid={`adv-custom-input-${ruleId}-${idx}`}
        />
      </div>
    </div>
  );
};


// ──────────────────────────────────────────────────────────────────────
// Add-Condition dropdown.
// ──────────────────────────────────────────────────────────────────────
const AddConditionDropdown = ({ ruleId, disabled, existing, onAdd }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <Button
        size="sm"
        variant="outline"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="gap-1 text-[11px]"
        data-testid={`adv-add-button-${ruleId}`}
      >
        <Plus className="h-3.5 w-3.5" /> Add condition
      </Button>
      {open && (
        <div
          className="absolute z-10 mt-1 w-[260px] rounded-md border border-border bg-background shadow-md p-1"
          onMouseLeave={() => setOpen(false)}
          data-testid={`adv-add-dropdown-${ruleId}`}
        >
          {CURATED_TYPES.map((t) => {
            const inUse = (existing || []).some((c) => c.context_type === t.value);
            return (
              <button
                key={t.value}
                onClick={() => { onAdd(t.value); setOpen(false); }}
                className="block w-full text-left px-2 py-1.5 rounded hover:bg-muted/50 text-[12px]"
                data-testid={`adv-add-${ruleId}-${t.value}`}
              >
                <div className="font-medium text-foreground flex items-center gap-1">
                  {t.label}
                  {inUse && <span className="text-[9px] uppercase tracking-wider text-muted-foreground">· already used</span>}
                </div>
                <div className="text-[10px] text-muted-foreground">{t.hint}</div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};


// ──────────────────────────────────────────────────────────────────────
// "Add Custom Context" inline form.
// Custom context is reserved for *new dimensions* that don't fit any
// curated type. The reserved-key validator stops admins from shadowing
// curated types via the back door (`custom:portal_channel` would never
// match real runtime contexts because resolvers populate `portal_channel`).
// ──────────────────────────────────────────────────────────────────────
const CustomContextRow = ({ ruleId, disabled, existing, onCancel, onConfirm }) => {
  const [key, setKey] = useState('');
  const normalize = (raw) => String(raw || '').toLowerCase().replace(/[^a-z0-9_]/gi, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
  const normalized = normalize(key);
  const reserved = RESERVED_CURATED_KEYS.has(normalized);
  const dup = (existing || []).some((c) => c.context_type === 'custom' && c.custom_key === normalized);
  const error = !normalized
    ? null
    : reserved
      ? `'${normalized}' is reserved for a curated type. Use the matching curated dropdown instead.`
      : dup
        ? `Already added a custom '${normalized}' condition on this rule.`
        : null;
  const valid = !!normalized && !error;
  return (
    <div className="flex items-start gap-1.5 flex-wrap" data-testid={`adv-custom-prompt-${ruleId}`}>
      <div className="flex flex-col">
        <Input
          value={key}
          onChange={(e) => setKey(e.target.value.replace(/[^a-z0-9_]/gi, '_').toLowerCase())}
          placeholder="e.g. is_visa_signature"
          disabled={disabled}
          className={cn('h-7 text-[11px] w-[220px]', error && 'border-rose-500 focus-visible:ring-rose-500/30')}
          data-testid={`adv-custom-key-input-${ruleId}`}
        />
        <div className="text-[10px] text-muted-foreground mt-1 leading-snug max-w-[280px]">
          {error ? (
            <span className="text-rose-500" data-testid={`adv-custom-key-error-${ruleId}`}>{error}</span>
          ) : (
            <>For a <b>new dimension</b> that doesn't fit Portal/Channel or Subcategory. Examples: <code className="text-[10px]">is_visa_signature</code>, <code className="text-[10px]">corporate_booking_channel</code>.</>
          )}
        </div>
      </div>
      <Button
        size="sm"
        variant="outline"
        disabled={disabled || !valid}
        onClick={() => onConfirm(normalized)}
        className="h-7 text-[11px]"
        data-testid={`adv-custom-confirm-${ruleId}`}
      >
        Add
      </Button>
      <Button
        size="sm"
        variant="ghost"
        disabled={disabled}
        onClick={onCancel}
        className="h-7 text-[11px]"
        data-testid={`adv-custom-cancel-${ruleId}`}
      >
        Cancel
      </Button>
    </div>
  );
};
